/**
 * In-memory Audit Hub mock matching the Audit Hub OpenAPI request/response shapes.
 * Used when REACT_APP_AUDIT_HUB_MOCK is not "false".
 */

import { AUDIT_STATUSES } from "../constants";

const STORAGE_KEY = "cloudcatcher:audit-hub-openapi-mock";

const okGet = (body) => body;
const okPost = (body, status = 200) => ({ data: body, status });
const fail = (status, code, message, field = null) => ({
  status,
  data: { error: { code, message, field } },
});

const load = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return { audits: [], jobs: {}, pollCount: {} };
    const parsed = JSON.parse(raw);
    return {
      audits: Array.isArray(parsed.audits) ? parsed.audits : [],
      jobs: parsed.jobs && typeof parsed.jobs === "object" ? parsed.jobs : {},
      pollCount: parsed.pollCount && typeof parsed.pollCount === "object" ? parsed.pollCount : {},
    };
  } catch (_e) {
    return { audits: [], jobs: {}, pollCount: {} };
  }
};

const save = (state) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (_e) {
    /* ignore */
  }
};

const emptyProgress = () => ({ total: 0, decided: 0, inReview: 0, outstanding: 0 });
const emptyComponents = () => ({
  passedControls: 0,
  failedControls: 0,
  manualPassed: 0,
  manualFailed: 0,
  manualNotApplicable: 0,
  manualUndecided: 0,
});

const CATALOG = [
  { complianceAddonId: 1, complianceKey: "cis_5.0_aws", name: "CIS", isScanEnabled: true, manualControlCount: 12 },
  { complianceAddonId: 2, complianceKey: "nist_csf_2.0_aws", name: "NIST CSF", isScanEnabled: true, manualControlCount: 8 },
  { complianceAddonId: 3, complianceKey: "soc2_aws", name: "SOC 2", isScanEnabled: true, manualControlCount: 0 },
];

const toSummary = (detail) => ({
  auditId: detail.auditId,
  auditCode: detail.auditCode,
  name: detail.name,
  status: detail.status,
  accountCount: detail.accountCount ?? (detail.accounts || []).length,
  scopeCount:
    detail.scopeCount ??
    (detail.accounts || []).reduce((sum, account) => sum + (account.scopes || []).length, 0),
  ownerEmail: detail.ownerEmail,
  dueDate: detail.dueDate ?? null,
  progress: detail.progress || emptyProgress(),
  baseScore: detail.baseScore ?? null,
  auditScore: detail.auditScore ?? null,
  lastActivityAt: detail.lastActivityAt ?? null,
  isStale: Boolean(detail.isStale),
});

const nextCode = (audits) => {
  const max = audits.reduce((acc, item) => {
    const match = String(item.auditCode || "").match(/^AUD-(\d+)$/i);
    return match ? Math.max(acc, Number(match[1])) : acc;
  }, 100);
  return `AUD-${String(max + 1).padStart(3, "0")}`;
};

const findAudit = (state, auditId) => state.audits.find((a) => a.auditId === auditId);

const containsAccount = (detail, accountNumber) =>
  (detail.accounts || []).some((account) => account.accountNumber === accountNumber) ||
  detail.accountNumber === accountNumber;

const containsFramework = (detail, complianceKey) =>
  (detail.accounts || []).some((account) =>
    (account.scopes || []).some((scope) => scope.framework?.complianceKey === complianceKey)
  ) || detail.framework?.complianceKey === complianceKey;

export const mockListFrameworks = async (params = {}) => {
  const page = Number(params.page) || 1;
  const size = Number(params.size) || 100;
  const start = (page - 1) * size;
  return okGet({
    page,
    size,
    total: CATALOG.length,
    items: CATALOG.slice(start, start + size),
  });
};

export const mockListAudits = async (params = {}) => {
  const state = load();
  let items = state.audits.map(toSummary);
  const details = state.audits;
  if (params.status) {
    items = items.filter((a) => a.status === params.status);
  }
  if (params.accountNumber) {
    const ids = new Set(
      details.filter((detail) => containsAccount(detail, params.accountNumber)).map((d) => d.auditId)
    );
    items = items.filter((a) => ids.has(a.auditId));
  }
  if (params.complianceKey) {
    const ids = new Set(
      details.filter((detail) => containsFramework(detail, params.complianceKey)).map((d) => d.auditId)
    );
    items = items.filter((a) => ids.has(a.auditId));
  }
  const page = Number(params.page) || 1;
  const size = Number(params.size) || 25;
  const start = (page - 1) * size;
  return okGet({
    page,
    size,
    total: items.length,
    items: items.slice(start, start + size),
  });
};

export const mockCreateAudit = async (body = {}) => {
  if (!body.name || !body.ownerEmail || !Array.isArray(body.accounts) || body.accounts.length < 1) {
    return fail(400, "VALIDATION_ERROR", "name, ownerEmail and accounts are required.");
  }
  const seenAccounts = new Set();
  const accounts = [];
  for (const entry of body.accounts) {
    if (!/^\d{12}$/.test(String(entry.accountNumber || ""))) {
      return fail(400, "VALIDATION_ERROR", "accountNumber must be 12 digits.", "accountNumber");
    }
    if (seenAccounts.has(entry.accountNumber)) {
      return fail(409, "ACCOUNT_ALREADY_IN_AUDIT", "The same account is listed twice.");
    }
    seenAccounts.add(entry.accountNumber);
    const ids = entry.complianceAddonIds;
    if (!Array.isArray(ids) || ids.length < 1) {
      return fail(400, "NO_SCOPES", "Each account needs at least one framework.", "complianceAddonIds");
    }
    const seenIds = new Set();
    const scopes = [];
    const auditAccountId = `aac-${Date.now().toString(36)}-${accounts.length}`;
    for (const rawId of ids) {
      const complianceAddonId = Number(rawId);
      if (!Number.isInteger(complianceAddonId)) {
        return fail(400, "VALIDATION_ERROR", "complianceAddonIds must be integers.", "complianceAddonIds");
      }
      if (seenIds.has(complianceAddonId)) {
        return fail(409, "FRAMEWORK_ALREADY_IN_SCOPE", "The same framework is listed twice on one account.");
      }
      seenIds.add(complianceAddonId);
      const catalog = CATALOG.find((item) => item.complianceAddonId === complianceAddonId);
      scopes.push({
        auditScopeId: `scp-${complianceAddonId}-${scopes.length}`,
        auditAccountId,
        framework: {
          complianceAddonId,
          complianceKey: catalog?.complianceKey || `addon-${complianceAddonId}`,
          name: catalog?.name || null,
          version: null,
        },
        hasScanResult: false,
        latestRunId: null,
        latestRunEndedAt: null,
        progress: emptyProgress(),
        baseScore: null,
        auditScore: null,
        components: emptyComponents(),
      });
    }
    accounts.push({
      auditAccountId,
      accountNumber: entry.accountNumber,
      accountName: null,
      accountCode: null,
      regions: Array.isArray(entry.regions) ? entry.regions : [],
      scheduleId: null,
      scopes,
    });
  }

  const state = load();
  const now = new Date().toISOString();
  const detail = {
    auditId: `aud-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    auditCode: nextCode(state.audits),
    name: body.name,
    status: AUDIT_STATUSES.ACTIVE,
    accountCount: accounts.length,
    scopeCount: accounts.reduce((sum, account) => sum + account.scopes.length, 0),
    ownerEmail: body.ownerEmail,
    dueDate: body.dueDate || null,
    progress: emptyProgress(),
    baseScore: null,
    auditScore: null,
    lastActivityAt: now,
    isStale: false,
    defaultAssigneeEmail: body.defaultAssigneeEmail || null,
    startedAt: now,
    completedAt: null,
    scoredScopeCount: 0,
    components: emptyComponents(),
    statusCounts: {},
    accounts,
    scanDispatchFailures: [],
  };
  state.audits.unshift(detail);
  save(state);
  return okPost(detail, 200);
};

export const mockGetAudit = async (auditId) => {
  const detail = findAudit(load(), auditId);
  if (!detail) return fail(404, "AUDIT_NOT_FOUND", "Audit not found.");
  return okGet(detail);
};

export const mockUpdateAudit = async (auditId, body = {}) => {
  const state = load();
  const detail = findAudit(state, auditId);
  if (!detail) return fail(404, "AUDIT_NOT_FOUND", "Audit not found.");
  if (detail.status !== AUDIT_STATUSES.ACTIVE) {
    return fail(409, "AUDIT_NOT_ACTIVE", "The audit is completed or cancelled.");
  }
  if (body.name != null) detail.name = body.name;
  if (body.ownerEmail != null) detail.ownerEmail = body.ownerEmail;
  if (body.defaultAssigneeEmail !== undefined) detail.defaultAssigneeEmail = body.defaultAssigneeEmail;
  if (body.dueDate !== undefined) detail.dueDate = body.dueDate;
  detail.lastActivityAt = new Date().toISOString();
  save(state);
  return okGet(detail);
};

export const mockCompleteAudit = async (auditId) => {
  const state = load();
  const detail = findAudit(state, auditId);
  if (!detail) return fail(404, "AUDIT_NOT_FOUND", "Audit not found.");
  detail.status = AUDIT_STATUSES.COMPLETED;
  detail.completedAt = new Date().toISOString();
  detail.lastActivityAt = detail.completedAt;
  save(state);
  return okPost(detail);
};

export const mockCancelAudit = async (auditId, body = {}) => {
  if (!body.reason) return fail(400, "VALIDATION_ERROR", "reason is required.", "reason");
  const state = load();
  const detail = findAudit(state, auditId);
  if (!detail) return fail(404, "AUDIT_NOT_FOUND", "Audit not found.");
  detail.status = AUDIT_STATUSES.CANCELLED;
  detail.lastActivityAt = new Date().toISOString();
  save(state);
  return okPost(detail);
};

export const mockRequestReport = async (auditId) => {
  const state = load();
  const detail = findAudit(state, auditId);
  if (!detail) return fail(404, "AUDIT_NOT_FOUND", "Audit not found.");
  const job = {
    jobId: `job-${Date.now().toString(36)}`,
    kind: "GENERATE_REPORT",
    status: "QUEUED",
    createdAt: new Date().toISOString(),
    completedAt: null,
    auditId,
    downloadUrl: null,
    requestedBy: "mock@local",
    error: null,
  };
  state.jobs[job.jobId] = job;
  state.pollCount[job.jobId] = 0;
  save(state);
  return okPost(job, 202);
};

export const mockGetJob = async (auditId, jobId) => {
  const state = load();
  const job = state.jobs[jobId];
  if (!job || job.auditId !== auditId) return fail(404, "JOB_NOT_FOUND", "Job not found.");
  state.pollCount[jobId] = (state.pollCount[jobId] || 0) + 1;
  if (state.pollCount[jobId] >= 2 && job.status !== "READY" && job.status !== "FAILED") {
    job.status = "READY";
    job.completedAt = new Date().toISOString();
    job.downloadUrl = URL.createObjectURL(
      new Blob([`Audit report placeholder for ${auditId}`], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      })
    );
  }
  save(state);
  return okGet(job);
};
